import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import { Order, TableProps, EnhancedTableProps, EnhancedTableToolbarProps } from './types';
import './P4STable.scss';
import FormDialog from '../FormDialog/FormDialog';
import { FormDialogProps } from '../FormDialog/types';
import DeleteButton from '../Buttons/DeleteButton';
import { TextField } from '@material-ui/core';
import SearchOutlined from '@mui/icons-material/SearchOutlined';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

export function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
        onRequestSort?.(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {props.showActions && (
                    <TableCell key="actions" align="left" padding="normal">
                        Actions
                    </TableCell>
                )}
                {props.columns.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        {orderBy !== undefined ? (
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        ) : (
                            headCell.label
                        )}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

export const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
            }}
        >
            <Typography sx={{ flex: '1 1 100%' }} variant="h6" id="tableTitle" component="div">
                {props?.title}
            </Typography>
            {props.handleSearch ? (
                <TextField
                    className="search-bar"
                    id="searchBar"
                    variant="outlined"
                    label={props?.label || 'Search by email...'}
                    onChange={(e) => {
                        if (props.handleSearch) props.handleSearch(e.target.value);
                    }}
                    InputProps={{
                        endAdornment: <SearchOutlined />,
                    }}
                />
            ) : (
                ''
            )}
            {props.addParams ? <FormDialog {...props.addParams}></FormDialog> : ''}
        </Toolbar>
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const P4STable = <T extends Record<string, any>>(props: TableProps<T>) => {
    const [order, setOrder] = React.useState<Order>(props.order || 'asc');
    const [orderBy, setOrderBy] = React.useState<keyof T>(props.orderBy);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(props.rowsPerPage);
    const [searched, setSearched] = React.useState<string>('');

    const handleRequestSort = (_event: React.MouseEvent<unknown>, property: keyof T) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.data.length) : 0;

    const getEditParams = (row: { [key in keyof T]: string | number }): FormDialogProps | undefined => {
        if (!props.editParams) return props.editParams;
        const textFields = props.editParams.textFields?.map((item) => {
            return {
                ...item,
                defaultValue: row[item.id],
            };
        });
        return {
            ...props.editParams,
            textFields,
            handleSubmit: (data: object, setOpen: React.Dispatch<React.SetStateAction<boolean>>) => {
                if (props.editParams && props.editParams.handleSubmit)
                    props.editParams.handleSubmit({ ...data, uid: row.uid }, setOpen);
            },
        };
    };

    return (
        <div className="table-container">
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
                    <EnhancedTableToolbar
                        title={props.title}
                        addParams={props.addParams}
                        handleSearch={props.filterBy ? setSearched : undefined}
                    />
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table stickyHeader sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={'medium'}>
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy as string}
                                onRequestSort={handleRequestSort}
                                columns={props.columns}
                                showActions={props.handleDelete !== undefined || props.editParams !== undefined}
                            />

                            <TableBody>
                                {stableSort(props.data, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .filter((row) => {
                                        if (
                                            !searched ||
                                            !props.filterBy ||
                                            row[props.filterBy].toString().includes(searched)
                                        )
                                            return row;
                                    })
                                    .map((row, index) => {
                                        return (
                                            <TableRow hover tabIndex={-1} key={index}>
                                                {props.handleDelete || props.editParams ? (
                                                    <TableCell>
                                                        {props.handleDelete ? (
                                                            <DeleteButton
                                                                action={() => {
                                                                    if (props.handleDelete)
                                                                        props.handleDelete(row.uid as string);
                                                                }}
                                                            ></DeleteButton>
                                                        ) : (
                                                            ''
                                                        )}
                                                        {props.editParams ? (
                                                            <FormDialog {...getEditParams(row)}></FormDialog>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </TableCell>
                                                ) : (
                                                    ''
                                                )}
                                                {props.columns.map((column, index) => {
                                                    if (index === 0) {
                                                        return (
                                                            <TableCell
                                                                key={`${column.id}_${index}`}
                                                                component="th"
                                                                scope="row"
                                                                padding="none"
                                                            >
                                                                {row[column.id]}
                                                            </TableCell>
                                                        );
                                                    } else {
                                                        return (
                                                            <TableCell key={`${column.id}_${index}`} align="right">
                                                                {row[column.id]}
                                                            </TableCell>
                                                        );
                                                    }
                                                })}
                                            </TableRow>
                                        );
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 53 * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={props.pages}
                        component="div"
                        count={props.data.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
        </div>
    );
};

export default P4STable;
